// frontend/src/pages/PVForm.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

const empty = {
  name: "",
  referent: "",
  address: "",
  telefono: "",
  fatt_email: "",
  fatt_piva: "",
  iban: "",
  banca: "",
  note_admin: "",
  username: "",
  password: "",
  email: "",
};

const PVForm = () => {
  const { id } = useParams();
  const editMode = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(editMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ---- helpers ----
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/pv");
  };

  // Esc -> torna indietro
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") goBack(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔐 solo MADRE
  useEffect(() => {
    (async () => {
      try {
        const me = await authFetch(`${API_URL}/api/me/`);
        const j = await me.json();
        const role = (j?.role || "").toUpperCase();
        if (role !== "MADRE") {
          alert("Accesso negato: solo la SEDE può gestire i Punti Vendita.");
          navigate("/pv", { replace: true });
        }
      } catch {
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // caricamento iniziale in edit
  useEffect(() => {
    const load = async () => {
      if (!editMode) return;
      setLoading(true);
      try {
        const res = await authFetch(`${API_URL}/api/pv/${id}/`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || "Errore caricamento");
        setForm({
          ...empty,
          name: data.name || "",
          referent: data.referent || "",
          address: data.address || "",
          telefono: data.telefono || "",
          fatt_email: data.fatt_email || "",
          fatt_piva: data.fatt_piva || "",
          iban: data.iban || "",
          banca: data.banca || "",
          note_admin: data.note_admin || "",
          username: "", // opzionali in edit
          password: "",
          email: data.email || "",
        });
      } catch (e) {
        console.error(e);
        setError("Impossibile caricare i dati.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

      try {
        const { username, password, email, ...payload } = form;
        const url = editMode ? `${API_URL}/api/pv/${id}/` : `${API_URL}/api/pv/`;
        const method = editMode ? "PUT" : "POST";

        const res = await authFetch(url, { method, body: JSON.stringify(payload) });
        const json = await res.json();

        if (!res.ok) {
          setError(json?.detail || "Errore di salvataggio.");
          return;
        }

        const goId = editMode ? id : (json.id ?? json.pk ?? json.code);

        if ((username || "").trim() || (password || "").trim() || (email || "").trim()) {
          if (!(username || "").trim() || !(password || "").trim()) {
            setError("Per impostare le credenziali servono sia username che password.");
            return;
          }

          const credRes = await authFetch(`${API_URL}/api/pv/${goId}/credenziali/`, {
            method: "POST",
            body: JSON.stringify({
              username: username.trim(),
              password,
              email: (email || "").trim() || undefined,
            }),
          });
          const credJson = await credRes.json();
          if (!credRes.ok) {
            setError(credJson?.detail || "PV salvato, ma errore impostazione credenziali.");
            return;
          }
        }

        navigate(`/pv/${goId}`, { replace: true });
    } catch (e) {
      console.error(e);
      setError("Errore imprevisto.");
    } finally {
      setSaving(false);
    }
  };

  // 🗑️ elimina punto vendita (solo edit)
  const onDelete = async () => {
    if (!editMode) return;
    if (!window.confirm("Sei sicuro di voler eliminare questo punto vendita?")) return;

    try {
      setSaving(true);
      setError("");
      const res = await authFetch(`${API_URL}/api/pv/${id}/`, { method: "DELETE" });
      // il backend può rispondere 204 senza body
      if (!res.ok && res.status !== 204) {
        let msg = "Impossibile eliminare il punto vendita.";
        try {
          const t = await res.text();
          if (t) msg = JSON.parse(t)?.detail || t || msg;
        } catch {}
        throw new Error(msg);
      }
      navigate("/pv", { replace: true });
    } catch (e) {
      console.error(e);
      alert(e.message || "Errore eliminazione punto vendita.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container mt-4"><p>Caricamento…</p></div>;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0">
          {editMode ? "✏️ Modifica Punto Vendita" : "➕ Nuovo Punto Vendita"}
        </h3>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={goBack}
          title="Torna indietro (Esc)"
        >
          ← Indietro
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form className="row g-3" onSubmit={submit}>
        <div className="col-md-6">
          <label className="form-label">Nome</label>
          <input className="form-control" name="name" value={form.name} onChange={onChange} required />
        </div>
        <div className="col-md-6">
          <label className="form-label">Referente</label>
          <input className="form-control" name="referent" value={form.referent} onChange={onChange} />
        </div>

        <div className="col-md-12">
          <label className="form-label">Indirizzo</label>
          <input className="form-control" name="address" value={form.address} onChange={onChange} />
        </div>

        <div className="col-md-4">
          <label className="form-label">Telefono</label>
          <input className="form-control" name="telefono" value={form.telefono} onChange={onChange} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Email fatturazione</label>
          <input type="email" className="form-control" name="fatt_email" value={form.fatt_email} onChange={onChange} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Partita IVA</label>
          <input className="form-control" name="fatt_piva" value={form.fatt_piva} onChange={onChange} />
        </div>

        <div className="col-md-6">
          <label className="form-label">IBAN</label>
          <input className="form-control" name="iban" value={form.iban} onChange={onChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label">Banca</label>
          <input className="form-control" name="banca" value={form.banca} onChange={onChange} />
        </div>

        <div className="col-md-12">
          <label className="form-label">Note amministrative</label>
          <textarea className="form-control" rows={3} name="note_admin" value={form.note_admin} onChange={onChange} />
        </div>

        <hr className="my-3" />

        <div className="col-12">
          <h5>🔐 Credenziali accesso</h5>
          <p className="text-muted mb-2">
            {editMode
              ? "Compila solo i campi che vuoi aggiornare (lascia vuoti per non cambiare)."
              : "Username e password sono obbligatori per creare l’account della Punto Vendita."}
          </p>
        </div>

        <div className="col-md-4">
          <label className="form-label">Username</label>
          <input
            className="form-control"
            name="username"
            value={form.username}
            onChange={onChange}
            required={!editMode}
            autoComplete="off"
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Password</label>
          <input
            type="password"
            className="form-control"
            name="password"
            value={form.password}
            onChange={onChange}
            required={!editMode}
            autoComplete="new-password"
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Email account</label>
          <input type="email" className="form-control" name="email" value={form.email} onChange={onChange} />
        </div>

        <div className="col-12 d-flex gap-2 justify-content-between">
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Salvataggio…" : (editMode ? "Salva" : "Crea")}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={goBack}>
              Annulla
            </button>
          </div>

          {editMode && (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={onDelete}
              disabled={saving}
              title="Elimina definitivamente questo punto vendita"
            >
              🗑️ Elimina
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PVForm;
